import api from 'api';
import type { ExportWorksheet } from 'components/superreports/ReportExport/types';
import config from 'config';
import { UNRESTRICTED_FROM, UNRESTRICTED_TO } from 'constants/datetime';
import type { SupportedExtensions } from 'constants/export';
import type { ColumnModeOptions } from 'redux/modules/accounts.types';
import type {
  ReportExportSettingsPayload,
  ReportResources,
} from 'redux/modules/superreports.types';
import type { Hour } from 'types/hour';
import type { HoursDetailsFilter } from 'types/hours';
import type { LightProject, Project } from 'types/project';
import type {
  EntityDataKey,
  EntityDataQuery,
  EntityDataResponse,
  PublicReportEntity,
  ReportEntity,
  ReportFilter,
  SharedReportsFilter,
  WidgetMetadata,
} from 'types/reports';
import type { DeepPartial } from 'types/utility';
import { to2upto } from 'utils/report';
import { objectToQueryString } from 'utils/url-params';

const reportsApi = {
  getReportData<R = EntityDataResponse>(
    query: EntityDataQuery,
    entities?: Array<EntityDataKey>,
    abortSignal?: AbortSignal
  ): Promise<R> {
    const groupBy = entities?.join(',');
    return api.post(
      `reports/filter.json${groupBy != null ? `?group_by=${groupBy}` : ''}`,
      {
        ...query,
        published: true,
        status: 'all',
      },
      { abortSignal }
    );
  },

  // Same as above, abstracted to improve self-documentation?
  getReportHours(query: EntityDataQuery, entities?: Array<EntityDataKey>) {
    return this.getReportData<Array<Hour<LightProject>>>(
      { ...query, projects_version: 3 },
      entities
    );
  },

  getReports(filter: Partial<ReportFilter>): Promise<Array<ReportEntity>> {
    return api.get(`reports.json?${objectToQueryString(filter)}`);
  },

  getPublicReports(filter: Partial<SharedReportsFilter>): Promise<Array<PublicReportEntity>> {
    const payload = {
      user_ids: filter.userIds || undefined,
      since: filter.from ?? null,
      upto: filter.to ? to2upto(filter.to) : null,
      limit: filter.limit ?? 50,
      offset: filter.offset ?? 0,
      order: filter.order ?? 'desc',
      live: filter.report_type === 'live',
      sort_by: filter.sort_by ?? 'created_at',
    };
    return api.get(`shared_reports.json?${objectToQueryString(payload)}`);
  },

  getReportById(id: number): Promise<ReportEntity> {
    return api.get(`reports/${id}.json`);
  },

  // --- REPORT EXPORT V2 ---
  getReportExportSettings(reportId: number): Promise<ReportExportSettingsPayload> {
    return api.get(`reports/${reportId}/report_setting`);
  },
  saveReportExportSettings(reportId: number, payload: ReportExportSettingsPayload) {
    return api.put(`reports/${reportId}/report_setting`, { report_setting: payload });
  },

  fetchNextExportDate(reportId: number, payload: ReportExportSettingsPayload) {
    return api.put(`reports/${reportId}/report_setting/next_occurrence`, {
      report_setting: payload,
    });
  },

  exportReport(
    id: number,
    format: string,
    options: {
      columns: Array<string>;
      worksheets: Array<ExportWorksheet>;
      time_entries: boolean;
      show_excluded_columns: boolean;
      tags_mode: ColumnModeOptions;
      external_link_filter?: string;
      sync_status_filter?: string;
    }
  ) {
    const {
      columns,
      worksheets,
      time_entries,
      show_excluded_columns,
      tags_mode,
      external_link_filter,
      sync_status_filter,
    } = options;
    const params: Record<string, unknown> = {
      export: format,
      fields: columns.join(','),
      worksheets,
      time_entries,
      show_excluded_columns,
      tags_mode,
      version: 3,
    };

    if (external_link_filter) {
      params.external_link_filter = external_link_filter;
    }
    if (sync_status_filter) {
      params.sync_status_filter = sync_status_filter;
    }

    return api.get(`reports/${id}/download.json?${objectToQueryString(params)}`, {
      filename: `hours-report.${format}`,
    });
  },

  saveReport(payload: DeepPartial<ReportEntity<string>>): Promise<ReportEntity> {
    if (payload.id) {
      return api.put(`reports/${payload.id}.json`, { report: payload });
    }
    return api.post('reports.json', { report: payload });
  },

  shareReportId(
    reportId: number,
    payload: Partial<PublicReportEntity>
  ): Promise<PublicReportEntity> {
    return api.post(`reports/${reportId}/share.json`, {
      shared_report: payload,
    });
  },

  deleteReport(reportId: number): Promise<void> {
    return api.delete(`reports/${reportId}.json`);
  },

  deletePublicReport(reportId: number): Promise<void> {
    return api.delete(`shared_reports/${reportId}.json`);
  },

  getReportExport(
    format: SupportedExtensions,
    filter: HoursDetailsFilter,
    emailReport = false,
    options: {
      totalsOnly?: boolean;
      entriesOnly?: boolean;
      resource?: ReportResources;
      timesheet?: boolean;
      tagsMode?: ColumnModeOptions;
    } = {}
  ) {
    const unrestricted = filter.timespan === 'unrestricted';
    const reportResource = options.resource ?? 'hours';
    const project_ids = Array.isArray(filter.projectIds)
      ? filter.projectIds.join(',')
      : filter.projectIds;
    const payload: any = {
      user_ids: filter.userIds.join(','),
      team_ids: filter.teamIds.join(','),
      label_ids: filter.labelIds.join(','),
      project_ids,
      project_ids_not_in:
        !Array.isArray(filter.projectIds) && filter.projectIdsNotIn?.length
          ? filter.projectIdsNotIn.join(',')
          : undefined,
      since: unrestricted ? UNRESTRICTED_FROM : filter.from,
      until: unrestricted ? UNRESTRICTED_TO : filter.to,
      export: format,
      resource: reportResource,
      state_ids: filter.hourStateIds.join(','),
      time_entries: true,
      order: filter.sortBy.direction,
      sort: filter.sortBy.field,
    };

    if (options.totalsOnly) {
      payload.scope = 'totals';
    }

    if (options.timesheet) {
      payload.timesheet = true;
      payload.show_excluded_columns = false;
    }

    // Without this the backend falls back to the account default and the table's own
    // "Tags display" selection is ignored (TT-5989).
    if (options.tagsMode) {
      payload.tags_mode = options.tagsMode;
    }

    if (options.entriesOnly) {
      payload.scope = 'entries';
    }

    if (payload.resource === 'hours') {
      payload.fields = filter.columns.join(',');
    } else if (payload.resource === 'forecasts') {
      payload.fields = filter.taskColumns.join(',');
    }

    if (filter.billedState != null) {
      payload.billed = filter.billedState;
    }

    if (filter.lockedState != null) {
      payload.locked = filter.lockedState;
    }

    if (filter.external_link_filter) {
      payload.external_link_filter = filter.external_link_filter;
    }

    if (filter.sync_status_filter) {
      payload.sync_status_filter = filter.sync_status_filter;
    }

    if (emailReport) {
      return api.post(`${reportResource}-reports.json`, payload);
    }

    return api.download('reports/download.json?version=3', payload, {
      filename: unrestricted
        ? `report.${format}`
        : `report-from-${filter.from}-to-${filter.to}.${format}`,
    });
  },
  getWidgetMetadata(): Promise<Array<WidgetMetadata>> {
    if (config.isPublicReport) {
      return api.get(`shared_reports/widgets`, {
        accountPrefix: false,
      });
    }

    return api.get('reports/widgets');
  },

  async getPublicReport(publicReportId: string): Promise<PublicReportEntity> {
    const publicReport: PublicReportEntity = await api.get(`shared_reports/${publicReportId}`, {
      accountPrefix: false,
    });

    // For very old public reports, the widgets may be stored as an escaped JSON string
    if (typeof publicReport.widgets === 'string') {
      try {
        publicReport.widgets = JSON.parse(publicReport.widgets);
      } catch {
        publicReport.widgets = [];
      }
    }

    return publicReport;
  },

  async getPublicReportData(publicReportId: string): Promise<Partial<EntityDataResponse>> {
    let response: Partial<EntityDataResponse> = await api.get(
      `shared_reports/${publicReportId}/filter`,
      {
        accountPrefix: false,
      }
    );

    // For very old public reports, the data may be stored as an escaped JSON string
    if (typeof response === 'string') {
      try {
        response = JSON.parse(response);
      } catch {
        response = {};
      }
    }

    return response;
  },

  getPublicReportHours(publicReportId: string): Promise<Array<Hour<Project>>> {
    return api.get(`shared_reports/${publicReportId}/filter?scope=events`, {
      accountPrefix: false,
    });
  },
  getPinnedReports(userIds?: Array<number>): Promise<Array<ReportEntity>> {
    return api.get(`reports/pinned.json?${objectToQueryString({ user_ids: userIds })}`);
  },
  pinReportId(reportId: number) {
    return api.post(`reports/${reportId}/pin.json`);
  },
  unpinReportId(reportId: number) {
    return api.delete(`reports/${reportId}/pin.json`);
  },
};

export default reportsApi;
